
import { getToken } from "utils/helper";
import { CustomApiResponse, PaginatedApiResponse,  } from "utils/helper/ApiResponses";
import { GetOperationsQuery, Operation } from "utils/types/operationsTypes";
import { ApiService } from "../apiservices";
import { apiSrcUrl } from "../ApiRoutes";


/**
 * Fetches a paginated and filtered list of all financial operations.
 * @param params - The query parameters for filtering, sorting, and pagination.
 * @returns A promise that resolves to the paginated list of operations.
 */
export const getOperations = async (
  params: GetOperationsQuery
): Promise<PaginatedApiResponse<Operation>> => {
  const token = getToken();
  const query = new URLSearchParams();

  // Append query parameters if they exist
  if (params.page) query.append("page", params.page.toString());
  if (params.limit) query.append("limit", params.limit.toString());
  if (params.search) query.append("search", params.search);
  if (params.kind) query.append("kind", params.kind);
  if (params.sortBy) query.append("sortBy", params.sortBy);
  if (params.order) query.append("order", params.order);
  if (params.country) query.append("country", params.country);

  const url = `${apiSrcUrl}/private/operations?${query.toString()}`;

  const response = await ApiService.get<CustomApiResponse<PaginatedApiResponse<Operation>>>(url, {
    Authorization: `Bearer ${token}`,
  });

  if (response.data.status !== 'success' || !response.data.data) {
    throw new Error(response.data.message || "Failed to fetch operations.");
  }

  return response.data.data;
};

/**
 * Creates a new manual operation. Admin only.
 * @param payload - The data for the new manual operation.
 * @returns The newly created operation record.
 */
export const createOperation = async (payload: { amount: number; description: string; country: string; userId?: string; }): Promise<Operation> => {
  const token = getToken();
  const url = `${apiSrcUrl}/private/operations`;
  const response = await ApiService.post<CustomApiResponse<Operation>>(url, payload, {
    Authorization: `Bearer ${token}`,
  });
  if (response.data.status !== 'success' || !response.data.data) {
    throw new Error(response.data.message || "Failed to create operation.");
  }
  return response.data.data;
};

/**
 * Updates an existing manual operation. Admin only.
 * @param id - The ID of the operation to update.
 * @param payload - The fields to be updated.
 * @returns The updated operation record.
 */
export const updateOperation = async (id: string, payload: { amount?: number; description?: string; country?: string; }): Promise<Operation> => {
  const token = getToken();
  const url = `${apiSrcUrl}/private/operations/${id}`;
  const response = await ApiService.patch<CustomApiResponse<Operation>>(url, payload, {
    Authorization: `Bearer ${token}`,
  });
  if (response.data.status !== 'success' || !response.data.data) {
    throw new Error(response.data.message || "Failed to update operation.");
  }
  return response.data.data;
};

/**
 * Deletes a manual operation. Admin only.
 * @param id - The ID of the operation to delete.
 */
export const deleteOperation = async (id: string): Promise<void> => {
    const token = getToken();
    const url = `${apiSrcUrl}/private/operations/${id}`;
    const response = await ApiService.delete<CustomApiResponse<any>>(url, {
        Authorization: `Bearer ${token}`,
    });
    if (response.data.status !== 'success') {
        throw new Error(response.data.message || "Failed to delete operation.");
    }
};
